/****************************************************
 * Module Name:  maxMemoReminderPeriodDays.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Constants from "../../utility/constants";
import Select from "react-select";
import {
    Box,
    CircularProgress,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Utility from "../../utility/utility";
import { LoadingButton } from "@mui/lab";
import { v4 as uuidv4 } from "uuid";
import CommonSnackbar from "../common/commonSnackBar";
import MemoDetails from "../common/memoDetails";
import NoPermission from "../common/noPermission";
import renderCellExpand from "../common/dataGridCellExpand";
import PageTitle from "../common/pageTitle";

const fullOptionToValue = (selectedData) => {
    if (selectedData.value === "*") return [];
    let newArr = [];
    selectedData.map((option) => {
        newArr.push(option.value);
        return true;
    });
    return newArr.includes("*") ? [] : newArr;
};

const MaxMemoReminderPeriodDays = () => {
    const [location, setLocation] = useState([]);
    const [locationOption, setLocationOption] = useState([]);
    const [listData, setListData] = useState([]);
    const [userReportListLoader, setUserReportListLoader] = useState(false);
    const [fetchData, setFetchData] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const defaultOptions = { value: "*", label: "-- ANY --" };
    const [access, setAccess] = useState(false);
    const [memoDetailModal, setMemoDetailModal] = useState(false);
    const [memoId, setMemoId] = useState('');
    const [filterValues, setFilterValues] = useState({});

    const getAllLocationData = () => {
        LocationProxy.GetAllLocationDetailMini((response) => {
            setDataLocation(response);
            setFetchData(true);
        });
    };

    useEffect(() => {
        PageTitle("REPORT_USERS_MAXMEMOREMINDERPERIODSDAYS");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.REPORT_USERS_MAXMEMOREMINDERPERIODSDAYS
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            getAllLocationData();
        }
    }

    useEffect(() => {
        if (fetchData) {
            if (location) {
                handleSearch(location);
            }
        }
    }, [fetchData, location]);

    const handleSearch = (location) => {
        setUserReportListLoader(true);
        let nLocation = fullOptionToValue(location);
        ReportProxy.GetMaxMemoReminderPeriodDays(
            nLocation,
            (response) => {
                const uniqueFilterValues = {
                    username: Utility.getDistinctOptions(response, "username"),
                    customername: Utility.getDistinctOptions(response, "customername"),
                    status: Utility.getDistinctOptions(response, "status"),
                    location: Utility.getDistinctOptions(response, "location"),
                };
                const combinedFilterValues = {
                    username: uniqueFilterValues.username.map(value => {
                        const matchingItem = response.find(item => item.username === value);
                        return matchingItem ? `${matchingItem.userid} | ${value}` : [];
                    }),
                    customername: uniqueFilterValues.customername.map(value => {
                        const matchingItem = response.find(item => item.customername === value);
                        return matchingItem ? `${matchingItem.customerid} | ${value}` : [];
                    }),
                    location: uniqueFilterValues.location.map(value => {
                        const matchingItem = response.find(item => item.location === value);
                        return matchingItem ? `${matchingItem.locationid} | ${value}` : [];
                    }),
                    status: uniqueFilterValues.status,
                };
                setFilterValues(combinedFilterValues);
                if (response) {
                    let data = response
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = uuidv4();
                    }
                    setListData(data);
                    setFetchData(false);
                    setUserReportListLoader(false);
                } else {
                    setUserReportListLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };

    const setDataLocation = (response) => {
        let optionsArray = [];
        optionsArray.push(defaultOptions);
        response.map((data) => {
            let option = { value: data.id, label: data.name + " | " + data.country };
            optionsArray.push(option);
            return true;
        });
        setLocationOption(optionsArray);
        setLocation(
            location.length !== 0 && optionsArray.length > 0 ? optionsArray[0] : location
        );
    };

    const handleClick = (params) => {
        setMemoId(params.row.memoid)
        setMemoDetailModal(true)
    }

    const initialColumns = () => [
        {
            field: "userid",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.username : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.userid + ' | ' + params.row.username,
            renderCell: renderCellExpand
        },
        {
            field: "customerid",
            headerName: "CUSTOMER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.customername : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.customerid + ' | ' + params.row.customername,
            renderCell: renderCellExpand
        },
        {
            field: "memoid",
            headerName: "MEMO ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            width: 100,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "updatedat",
            headerName: "UPDATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "duedate",
            headerName: "DUE DATE",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.locationid + ' | ' + params.row.location,
            renderCell: renderCellExpand
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 150,
        },
    ];

    const columns = initialColumns();

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {memoDetailModal ? (
                <MemoDetails
                    show={true}
                    handleClose={() => setMemoDetailModal(false)}
                    memoId={memoId}
                />
            ) : (
                <Box />
            )}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            sx={{
                                display: "flex",
                                width: "100vw",
                                mb: 3,
                                mt: 2,
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: "10px",
                                pl: 1
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    minWidth: "370px",
                                    maxWidth: "auto",
                                    gap: "20px",
                                }}
                            >
                                <Box sx={{ alignSelf: "center", mt: 1, width: "75px" }}>
                                    <Typography>Location</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        alignSelf: "center",
                                        minWidth: "280px",
                                        maxWidth: "450px",
                                        mt: 1,
                                    }}
                                >
                                    <Select menuPlacement="auto"
                                        blurInputOnSelect={false}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isSearchable={true}
                                        name="location"
                                        closeMenuOnSelect={false}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                        isMulti
                                        options={locationOption}
                                        value={location}
                                        menuPortalTarget={document.body}
                                        styles={Constants.Styles}
                                        onChange={(selectedOption) => setLocation(selectedOption)}
                                    />
                                </Box>
                            </Box>
                            <LoadingButton
                                loading={userReportListLoader}
                                loadingPosition="start"
                                variant="contained"
                                onClick={() => handleSearch(location)}
                                startIcon={<SearchOutlinedIcon size="large" />}
                                sx={{ mt: 1 }}
                                disabled={
                                    locationOption.length === 0
                                }
                            >
                                Search
                            </LoadingButton>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "80vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={listData}
                                columns={columns}
                                loading={userReportListLoader}
                                getRowId={(row) => row.id}
                                onRowClick={handleClick}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                }}
                            >
                                {userReportListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() =>
                                                handleSearch(location)
                                            }
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}
        </div>
    );
};

export default MaxMemoReminderPeriodDays;
